import React, { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Input,
  Table,
  Tag,
  Space,
  message,
  Card,
  Spin,
  Row,
  Col,
  Grid,
  List
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusOutlined,
  EditOutlined,
  CloseCircleOutlined,
  UserOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Visitor, VisitorStatus, VisitorType } from '@/types/residents/visitor';
import { getVisitors, addVisitor, updateVisitor } from '@/api/residents';
import VisitorModal from '@/components/features/residents/VisitorModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 10;
const { useBreakpoint } = Grid;

export default function VisitorManagementPage() {
  const { t } = useTranslation(['visitors', 'common']);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { firestoreUser } = useAuth();
  const screens = useBreakpoint();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState<Visitor | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Fetch visitors
  const { data, isLoading } = useQuery({
    queryKey: ['visitors', page, searchTerm],
    queryFn: () => getVisitors(PAGE_SIZE, undefined, firestoreUser || undefined, searchTerm, page),
    staleTime: 5 * 60 * 1000,
  });

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data: Omit<Visitor, 'id'>) => addVisitor(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('visitors:createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
      setIsModalOpen(false);
    },
    onError: () => {
      message.error(t('visitors:createError'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Visitor> }) =>
      updateVisitor(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('visitors:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
      setIsModalOpen(false);
    },
    onError: () => {
      message.error(t('visitors:updateError'));
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingVisitor(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((visitor: Visitor) => {
    setEditingVisitor(visitor);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((visitor: Visitor) => {
    navigate(`/residents/visitors/${visitor.id}`, { state: { visitor } });
  }, [navigate]);

  const handleDelete = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleModalSuccess = useCallback((data: Omit<Visitor, 'id'>) => {
    if (editingVisitor) {
      updateMutation.mutate({ id: editingVisitor.id, data });
    } else {
      addMutation.mutate(data);
    }
  }, [editingVisitor, updateMutation, addMutation]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page when searching
  }, []);

  // Memoized values
  const filteredData = useMemo(() => {
    if (!data?.visitors) return [];
    return data.visitors;
  }, [data?.visitors]);

  const getStatusColor = (status: VisitorStatus) => {
    const colors: Record<VisitorStatus, string> = {
      pending: 'warning',
      approved: 'processing',
      rejected: 'error',
      checked_in: 'success',
      checked_out: 'default',
      expired: 'default',
    };
    return colors[status];
  };

  const getTypeColor = (type: VisitorType) => {
    const colors: Record<VisitorType, string> = {
      guest: 'blue',
      service: 'green',
      delivery: 'orange',
      contractor: 'purple',
      other: 'default',
    };
    return colors[type];
  };

  const renderVisitorCard = (visitor: Visitor) => (
    <Card
      key={visitor.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(visitor)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(visitor)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<CloseCircleOutlined />}
          danger
          onClick={() => handleDelete()}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={
          <Space>
            <UserOutlined />
            {visitor.name}
          </Space>
        }
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color={getTypeColor(visitor.type)}>
                {t(`visitors:types.${visitor.type}`)}
              </Tag>
              <Tag color={getStatusColor(visitor.status)}>
                {t(`visitors:statuses.${visitor.status}`)}
              </Tag>
            </div>
            <div>{t('visitors:visitingResident')}: {visitor.resident?.name || '-'}</div>
            <div>{t('visitors:expectedArrival')}: {visitor.expectedArrival ? dayjs(visitor.expectedArrival).format('YYYY-MM-DD HH:mm') : '-'}</div>
          </Space>
        }
      />
    </Card>
  );

  const columns = [
    {
      title: t('visitors:name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: t('visitors:type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: VisitorType) => (
        <Tag color={getTypeColor(type)}>
          {t(`visitors:types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('visitors:status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: VisitorStatus) => (
        <Tag color={getStatusColor(status)}>
          {t(`visitors:statuses.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('visitors:visitingResident'),
      dataIndex: ['resident', 'name'],
      key: 'visitingResident',
      render: (name: string) => name || '-',
    },
    {
      title: t('visitors:expectedArrival'),
      dataIndex: 'expectedArrival',
      key: 'expectedArrival',
      render: (date: Date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a: Visitor, b: Visitor) => 
        (a.expectedArrival?.getTime() ?? 0) - (b.expectedArrival?.getTime() ?? 0),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Visitor) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            {t('common:actions.view')}
          </Button>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => handleDelete()}
            size="small"
          >
            {t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder={t('visitors:searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<UserOutlined />}
              style={{ width: screens.xs ? '100%' : 300 }}
            />
            <Button type="primary" onClick={handleAdd} icon={<PlusOutlined />}>
              {t('visitors:add')}
            </Button>
          </Space>
        </Col>
      </Row>

      
      {screens.xs ? (
        <List
          dataSource={filteredData}
          renderItem={renderVisitorCard}
          rowKey="id"
          loading={isLoading}
          grid={{ gutter: 16, xs: 1, sm: 1 }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.total || 0,
            onChange: (newPage) => setPage(newPage),
            showSizeChanger: false,
          }}
          scroll={{ x: 'max-content' }}
        />
      )}


      {/* Modals */}
      <VisitorModal
        open={isModalOpen}
        visitor={editingVisitor}
        onSuccess={handleModalSuccess}
        onCancel={() => setIsModalOpen(false)}
      />

      <DeleteConfirmationModal
        visible={isDeleteModalOpen}
        title={t('visitors:deleteConfirmTitle')}
        onConfirm={() => {
          // Handle delete confirmation
          setIsDeleteModalOpen(false);
        }}
        onCancel={() => {
          setIsDeleteModalOpen(false);
        }}
      />
    </div>
  );
} 